import {useApolloClient} from '@apollo/client';
import {RawTransactionConsumerIdsDocument} from '@transactions/graphql';
import * as React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import type {
  BasicRawTransactionFragment,
  RawTransactionConsumerIdsQuery,
  RawTransactionConsumerIdsQueryVariables,
} from '@transactions/graphql';
import type {SelectionState} from 'src/useSelectionState';

/**
 * The page a transaction opens to with one of its lines picked out — where a
 * tap on an allocated activity line lands.
 */
export const transactionLineHref = (transactionId: string, lineId: string) =>
  `/transactions/${transactionId}?line=${encodeURIComponent(lineId)}`;

/**
 * What a tap on an activity line opens. A line nothing consumes opens the
 * create sheet, since allocating it is the only thing to do with it. A line
 * one transaction accounts for opens that transaction, with the line picked
 * out. A line split across several transactions has no single answer, so it
 * opens its own page, which lists them.
 *
 * Which of those it is takes asking for the line's consumers: a remainder
 * equal to the amount doesn't prove there are none, since consumptions can
 * net to zero. The review queue, where every line is waiting on an
 * allocation, opens the sheet without asking.
 */
export const useOpenRawTransaction = ({
  openCreate,
  allocateOnTap = false,
}: {
  /** Opens the create sheet on one row, with the list's own bookkeeping. */
  openCreate: (rt: BasicRawTransactionFragment) => void;
  allocateOnTap?: boolean;
}) => {
  const client = useApolloClient();
  const navigate = useNavigate();
  // A lookup acts only if nothing happened while it was out: another tap,
  // any other gesture on the list (`cancel`, which useRawRowGestures calls
  // on every one), leaving the view (a filter change moves the URL too), or
  // unmounting all retire it, so a late answer can't pull the user off what
  // they moved on to.
  const latestTap = React.useRef(0);
  const location = useLocation();
  React.useEffect(() => {
    latestTap.current += 1;
  }, [location.key]);
  React.useEffect(
    () => () => {
      latestTap.current += 1;
    },
    [],
  );

  const cancel = React.useCallback(() => {
    latestTap.current += 1;
  }, []);

  const open = React.useCallback(
    (rt: BasicRawTransactionFragment) => {
      const tap = ++latestTap.current;
      if (allocateOnTap) {
        openCreate(rt);
        return;
      }
      void client
        .query<
          RawTransactionConsumerIdsQuery,
          RawTransactionConsumerIdsQueryVariables
        >({
          query: RawTransactionConsumerIdsDocument,
          variables: {id: rt._id},
          // Allocation changes from many places; a stale answer would open a
          // transaction that no longer holds this line.
          fetchPolicy: 'network-only',
        })
        .then(({data}) => {
          if (tap !== latestTap.current) return;
          const ids = [
            ...new Set(data.rawTransaction.consumers.map((c) => c.transactionId)),
          ];
          if (ids.length === 0) {
            // Nothing to allocate on a fully consumed line; its page is all
            // there is to open.
            if ((rt.remainingAmount ?? rt.amount) !== 0) openCreate(rt);
            else navigate(`/raw-transactions/${rt._id}`);
            return;
          }
          navigate(
            ids.length === 1
              ? transactionLineHref(ids[0], rt._id)
              : `/raw-transactions/${rt._id}`,
          );
        })
        .catch(() => {
          if (tap === latestTap.current) navigate(`/raw-transactions/${rt._id}`);
        });
    },
    [client, navigate, openCreate, allocateOnTap],
  );

  return {open, cancel};
};

/**
 * The gestures every activity list shares: a tap opens the line, a long press
 * (or a tap on the row's leading edge) selects it, and once anything is
 * selected a tap toggles. `startSelecting` is the desktop Select button: it
 * shows the check column before anything is picked.
 */
export const useRawRowGestures = ({
  selectionState,
  openCreate,
  allocateOnTap,
}: {
  selectionState: SelectionState<BasicRawTransactionFragment>;
  openCreate: (rt: BasicRawTransactionFragment) => void;
  /** Every row is waiting on an allocation (see useOpenRawTransaction). */
  allocateOnTap?: boolean;
}) => {
  const {selected, setSelected, clearSelection} = selectionState;
  const [explicit, setExplicit] = React.useState(false);
  const selecting = explicit || selected.length > 0;
  const {open, cancel} = useOpenRawTransaction({openCreate, allocateOnTap});

  // A selection emptying — an action finished with it, or the last row was
  // unpicked — ends select mode too, so taps open rows again.
  const hadSelection = React.useRef(false);
  React.useEffect(() => {
    if (selected.length > 0) hadSelection.current = true;
    else if (hadSelection.current) {
      hadSelection.current = false;
      setExplicit(false);
    }
  }, [selected.length]);

  const selectedIds = React.useMemo(
    () => selected.map((rt) => rt._id),
    [selected],
  );

  // Every gesture but an opening tap retires a pending lookup first.
  const stopSelecting = React.useCallback(() => {
    cancel();
    setExplicit(false);
    clearSelection();
  }, [cancel, clearSelection]);

  const onClick = (rt: BasicRawTransactionFragment) => {
    if (selecting) {
      cancel();
      setSelected(rt)(!selectedIds.includes(rt._id));
      return;
    }
    open(rt);
  };

  const onLongPress = (rt: BasicRawTransactionFragment) => {
    cancel();
    setSelected(rt)(true);
  };

  return {
    selecting,
    startSelecting: () => {
      cancel();
      setExplicit(true);
    },
    stopSelecting,
    rowProps: {selectedIds, onClick, onLongPress, selectMode: selecting},
  };
};
